import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { useCampaignActions } from '../hooks/useCampaignActions';
import type { CampaignDetail } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { endWeeksOf, runsUntilOf } from './builderFormat';
import { QuestionPanel } from './QuestionPanel';
import { startMessages as messages } from './startMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
import who from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FOLLOW_UP_DAYS = 5;
const END_OPTIONS = ['4', '8', '12', 'never'] as const;

/**
 * "Timing" (design outreach-v2 f1): weekdays (the line says that a weekend pitch waits for Monday,
 * QA B07), one follow-up after 5 days, the end of the campaign
 * (4, 8 or 12 weeks after the release, or until the artist stops it) and the notifications.
 */
const TimingRowsComponent: React.FC<{ accountId: number; campaign: CampaignDetail; readOnly: boolean }> = ({
  accountId,
  campaign,
  readOnly,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { updateCampaign } = useCampaignActions(accountId);
  const save = (patch: Parameters<typeof updateCampaign>[0]) => updateCampaign(patch).catch(console.error);
  const weeks = endWeeksOf(campaign.endWeeks);
  const until = runsUntilOf({ ...campaign, endWeeks: weeks }, new Date());
  const { schedule } = campaign;
  const endOptions = useMemo(
    () =>
      END_OPTIONS.map((option) => (
        <option key={option} value={option}>
          {option === 'never'
            ? intl.formatMessage(messages.endNever)
            : intl.formatMessage(messages.endWeeks, { weeks: Number(option) })}
        </option>
      )),
    [intl]
  );

  return (
    <QuestionPanel title={intl.formatMessage(messages.timingTitle)} testId="builder-timing">
      <div className={styles.setRow}>
        <div className="flex-grow-1">
          <b>{intl.formatMessage(messages.windowTitle)}</b>
          <small>
            {intl.formatMessage(schedule.weekdaysOnly ? messages.windowSubWeekdays : messages.windowSub, {
              start: schedule.windowStartHour,
              end: schedule.windowEndHour,
            })}
          </small>
        </div>
        <select
          className="form-select form-select-sm w-auto"
          aria-label={intl.formatMessage(messages.windowLabel)}
          value={schedule.weekdaysOnly ? 'weekdays' : 'all'}
          disabled={readOnly}
          onChange={(event) =>
            save({ campaignUuid: campaign.campaignUuid, schedule: { weekdaysOnly: event.target.value === 'weekdays' } })
          }
        >
          <option value="weekdays">{intl.formatMessage(messages.weekdays)}</option>
          <option value="all">{intl.formatMessage(messages.everyDay)}</option>
        </select>
      </div>
      <div className={styles.setRow}>
        <div className="flex-grow-1">
          <b>{intl.formatMessage(messages.followTitle, { days: FOLLOW_UP_DAYS })}</b>
          <small>{intl.formatMessage(messages.followSub)}</small>
        </div>
        <input
          type="checkbox"
          role="switch"
          className={who.switch}
          aria-label={intl.formatMessage(messages.followLabel)}
          checked={campaign.followUpDays !== null}
          disabled={readOnly}
          onChange={(event) =>
            save({ campaignUuid: campaign.campaignUuid, followUpDays: event.target.checked ? FOLLOW_UP_DAYS : null })
          }
        />
      </div>
      <div className={styles.setRow}>
        <div className="flex-grow-1">
          <b>
            {until === null
              ? intl.formatMessage(messages.endTitleOpen)
              : intl.formatMessage(messages.endTitle, { date: shortDate(intl, until.toISOString()) })}
          </b>
          <small>
            {weeks === null ? intl.formatMessage(messages.endSubOpen) : intl.formatMessage(messages.endSub, { weeks })}
          </small>
        </div>
        <select
          className="form-select form-select-sm w-auto"
          aria-label={intl.formatMessage(messages.endLabel)}
          value={weeks === null ? 'never' : String(weeks)}
          disabled={readOnly}
          onChange={(event) =>
            save({
              campaignUuid: campaign.campaignUuid,
              endWeeks: event.target.value === 'never' ? null : endWeeksOf(Number(event.target.value)),
            })
          }
        >
          {endOptions}
        </select>
      </div>
      <div className={styles.setRow}>
        <div className="flex-grow-1">
          <b>{intl.formatMessage(messages.tellTitle)}</b>
          <small>{intl.formatMessage(messages.tellSub)}</small>
        </div>
        <SMLButton
          size="sm"
          outline
          omitCap
          onClick={() => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.notifications })}
        >
          {intl.formatMessage(messages.tellChange)}
        </SMLButton>
      </div>
    </QuestionPanel>
  );
};

export const TimingRows = TypedMemo(TimingRowsComponent);
